import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"

export type StatusConfig = UptimeConfigQuery["getStatusConfig"]

// Module-level cache for the editorial status configuration. The
// existing helper API in `uptime.utils.ts` (getCategoryLabel,
// getProviderName, comparePlatforms, …) is plain functions called
// from React render paths, table column accessors, and pure
// reducers in `uptime.buckets.utils.ts` — none of which can become
// hooks without rippling through the entire module graph. So we
// publish the config through a tiny mutable singleton instead.
//
// The flow:
//   1. UptimePage / UptimeDashboardView mounts useStatusConfigBootstrap.
//   2. The hook runs `getStatusConfig` and writes the result here.
//   3. Helper functions read through `getCachedStatusConfig()` in
//      their hot path; if the cache is empty (first paint, or the
//      query failed) they fall back to the baked-in literals so
//      the UI never goes blank.
//
// One-writer / many-reader pattern -- the cache is only ever
// updated by the bootstrap hook and only ever read from synchronous
// helper functions, so no synchronisation primitive is needed.

let cached: StatusConfig | null = null

// Lookup tables derived from the cached config; rebuilt every time
// the cache is replaced so each helper read is a single map lookup
// instead of an O(n) scan over Categories / Providers / Sources.
type LookupTables = {
  categoryBySource: Map<string, StatusConfig["Categories"][number]>
  platformById: Map<string, StatusConfig["Platforms"][number]>
  providerById: Map<string, StatusConfig["Providers"][number]>
  hostSuffixToProvider: Array<{ suffix: string; providerName: string }>
}

let cachedTables: LookupTables | null = null

function buildTables(config: StatusConfig): LookupTables {
  const categoryBySource = new Map<string, StatusConfig["Categories"][number]>()
  for (const c of config.Categories) {
    if (!c.IsEnabled) continue
    for (const src of c.Sources) {
      // First match wins; categories are deduplicated upstream by
      // the seed sproc but we guard here to keep this safe under
      // operator-driven edits that might temporarily duplicate a
      // source while a swap is in progress.
      if (!categoryBySource.has(src)) categoryBySource.set(src, c)
    }
  }

  const platformById = new Map<string, StatusConfig["Platforms"][number]>()
  for (const p of config.Platforms) platformById.set(p.Id, p)

  const providerById = new Map<string, StatusConfig["Providers"][number]>()
  for (const p of config.Providers) providerById.set(p.Id, p)

  // Flatten the host-suffix -> provider directory into a single
  // sorted array. Sort order mirrors the SQL `ORDER BY Priority,
  // LEN(HostSuffix) DESC` so the most-specific match wins on
  // priority ties; consumers walk the array top-to-bottom and pick
  // the first endsWith hit.
  const hostSuffixes: Array<{
    suffix: string
    providerName: string
    priority: number
  }> = []
  for (const provider of config.Providers) {
    if (!provider.IsEnabled) continue
    for (const hs of provider.HostSuffixes) {
      hostSuffixes.push({
        suffix: hs.HostSuffix.toLowerCase(),
        providerName: provider.Name,
        priority: hs.Priority,
      })
    }
  }
  hostSuffixes.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority
    return b.suffix.length - a.suffix.length
  })

  return {
    categoryBySource,
    platformById,
    providerById,
    hostSuffixToProvider: hostSuffixes.map((h) => ({
      suffix: h.suffix,
      providerName: h.providerName,
    })),
  }
}

export function setCachedStatusConfig(config: StatusConfig | null): void {
  cached = config
  cachedTables = config ? buildTables(config) : null
}

export function getCachedStatusConfig(): StatusConfig | null {
  return cached
}

export function getCachedStatusTables(): LookupTables | null {
  return cachedTables
}
